import { IGeneralSearch } from '@core/market-data-sdk';
import { formatCnpj, formatNumber } from '../../../utils/format.helper';
import dayjs from 'dayjs';

export class SearchItemEntity {
  public info: SearchExtraInfo[] = [];

  constructor(private _props: SearchItemProps) {
    this.createAdditionalInfos();
  }

  get canonical_url() {
    return this._props.canonical_url ? `/${this._props.canonical_url}` : null;
  }

  get identifier() {
    return this._props.identifier;
  }

  get nicename() {
    return this._props.nicename;
  }

  get type() {
    return this._props.type;
  }

  get hasQuotes(): boolean {
    return Boolean(
      this._props.additional_fields?.has_quotes ||
        this._props.additional_fields?.has_unadjusted_quotes
    );
  }

  private createAdditionalInfos() {
    const extra = this._props.additional_fields;

    this.info = [
      extra?.is_for_negotiation && {
        displayName: 'Situação',
        slug: 'status',
        value: extra?.is_for_negotiation ? 'Em negociação' : 'Vencido',
      },
      extra?.situation && {
        displayName: 'Situação',
        slug: 'situacao',
        value: this.formFundStatus(extra?.situation),
      },
      extra?.cnpj && {
        displayName: 'CNPJ',
        slug: 'cnpj',
        value: formatCnpj(extra?.cnpj),
      },
      extra?.first_quote && {
        displayName: 'Data de início',
        slug: 'first_quote',
        value: dayjs(extra?.first_quote).format('DD/MM/YYYY'),
        rawValue: extra?.first_quote,
      },
      extra?.last_quote && {
        displayName: 'Última Cota',
        slug: 'last_quote',
        value: dayjs(extra?.last_quote).format('DD/MM/YYYY'),
        rawValue: extra?.last_quote,
      },
      extra?.data_emissao && {
        displayName: 'Data de emissão',
        slug: 'data_emissao',
        value: dayjs(extra?.data_emissao).format('DD/MM/YYYY'),
      },
      extra?.dt_emissao && {
        displayName: 'Data de emissão',
        slug: 'dt_emissao',
        value: dayjs(extra?.dt_emissao).format('DD/MM/YYYY'),
      },
      extra?.emissao && {
        displayName: 'Nº da emissão',
        slug: 'emissao',
        value: extra?.emissao,
      },
      extra?.sector && {
        displayName: 'Setor',
        slug: 'sector',
        value: extra?.sector,
      },
      extra?.networth && {
        displayName: 'Patrimônio',
        slug: 'networth',
        value: formatNumber(extra?.networth, {
          type: 'money',
          thousandMask: true,
        }),
      },
      extra?.quota_holders && {
        displayName: 'Cotistas',
        slug: 'quota_holders',
        value: formatNumber(extra?.quota_holders, {
          thousandMask: true,
          precision: 0,
        }),
      },
      extra?.med_quant_neg && {
        displayName: 'Média Diária Negociada',
        slug: 'negotiation',
        value: formatNumber(extra?.med_quant_neg, { thousandMask: true }),
      },
      extra?.serie && {
        displayName: 'Série',
        slug: 'serie',
        value: extra?.serie,
      },
      extra?.dismemberment && {
        displayName: 'Desmembramento',
        slug: 'type',
        value: extra?.dismemberment,
      },
      extra?.deb_incentivada && {
        displayName: 'Incentivada',
        slug: 'deb_incentivada',
        value: extra?.deb_incentivada === 'S' ? 'Sim' : 'Não',
      },
    ].filter(Boolean) as SearchExtraInfo[];
  }

  private formFundStatus(status: string): string {
    switch (status) {
      case 'p':
        return 'Pré Operacional';

      case 'EM FUNCIONAMENTO NORMAL':
        return 'Operacional';
      case 'Em Funcionamento normal':
        return 'Operacional';

      case 'c':
        return 'Cancelado';

      default:
        return status;
    }
  }

  isDisabled(isSelectMode: boolean) {
    if (isSelectMode) {
      return !this.hasQuotes;
    }

    return !this._props.canonical_url;
  }

  addExtraInfo(info: any[]) {
    this.info = [...this.info, ...(info.filter(Boolean) as SearchExtraInfo[])];
  }

  toJson(): ISearchToJSON {
    return {
      ...this._props,
      info: this.info,
      has_quotes: this.hasQuotes,
    };
  }
}

export interface SearchItemProps extends IGeneralSearch {}

export type ISearchToJSON = SearchItemProps & {
  info: SearchExtraInfo[];
  has_quotes: boolean;
};

interface SearchExtraInfo {
  slug: string;
  displayName: string;
  value: string | number;
  rawValue?: string | number;
}
